import React, { useEffect, useMemo, useState } from 'react';
import {
  ButtonControl,
  DialogControl,
  FieldControl,
  IconControl,
  Loading,
  SearchInput,
} from 'spark-core-dx/components';
import { SystemTypes } from 'spark-core-dx/services';
import './activeSessionListControl.scss';

const read = (item, ...keys) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
  }
  return undefined;
};

export const activeSessionId = (item) => String(
  read(item, 'SessionId', 'sessionId') || '',
);

export const activeSessionKind = (item) => String(
  read(item, 'SessionKind', 'sessionKind') || '',
);

export const activeSessionIsActive = (item) => {
  const value = read(item, 'IsActive', 'isActive');
  if (typeof value === 'boolean') return value;
  return ['active', 'refreshing'].includes(String(
    read(item, 'Status', 'status') || '',
  ).toLowerCase());
};

const statusFilterField = {
  FieldName: 'ActiveSessionStatusFilter',
  fieldName: 'ActiveSessionStatusFilter',
  DisplayName: 'Status',
  displayName: 'Status',
  FieldType: SystemTypes.UIFieldType.SelectList,
  fieldType: SystemTypes.UIFieldType.SelectList,
  Format: SystemTypes.UIFormats?.STRING,
  DataJson: [
    { Text: 'Active', Value: 'Active' },
    { Text: 'Inactive', Value: 'Inactive' },
  ],
};

const onOrBeforeField = {
  FieldName: 'ActiveSessionOnOrBefore',
  fieldName: 'ActiveSessionOnOrBefore',
  DisplayName: 'On or before',
  displayName: 'On or before',
  HelpText: 'Include sessions whose last activity or issue date is on or before this date.',
  FieldType: SystemTypes.UIFieldType.Date,
  fieldType: SystemTypes.UIFieldType.Date,
  Format: SystemTypes.UIFormats?.DATE,
};

const normalizeStatusFilterValue = (value, selectedItem) => {
  const selectedValue = selectedItem?.Value
    ?? selectedItem?.value
    ?? value?.Value
    ?? value?.value
    ?? value?.target?.value
    ?? value;
  return String(selectedValue || 'Active').toLowerCase() === 'inactive'
    ? 'Inactive'
    : 'Active';
};

const normalizeDateValue = (value) => {
  if (!value) return '';
  if (typeof value?.format === 'function') return value.format('YYYY-MM-DD');
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTime = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const sessionTitle = (session) => (
  read(session, 'UserDisplayName', 'userDisplayName')
  || read(session, 'PersonalAccessTokenLabel', 'personalAccessTokenLabel')
  || 'Unresolved user'
);

const sessionSubtitle = (session) => (
  read(session, 'UserEmail', 'userEmail')
  || read(session, 'ApplicationDisplayName', 'applicationDisplayName')
  || read(session, 'McpServerDisplayName', 'mcpServerDisplayName')
  || 'No additional identity information'
);

const Fact = ({ label, value }) => value ? (
  <div className="active-session-list__fact">
    <dt>{label}</dt>
    <dd>{value}</dd>
  </div>
) : null;

export const ActiveSessionDetails = ({ session }) => {
  if (!session) return null;
  const scopes = read(session, 'Scopes', 'scopes') || [];
  const roles = read(session, 'UserRoles', 'userRoles') || [];
  const isActive = activeSessionIsActive(session);

  return (
    <div className="active-session-list__dialog">
      <p>
        {isActive
          ? 'This credential is currently valid. Last activity is shown exactly; it does not indicate that the user or MCP client is online now.'
          : 'This credential is inactive and cannot authenticate. Historical activity is shown exactly; it does not indicate that the user or MCP client is online now.'}
      </p>
      <dl className="active-session-list__detail-grid resource-workspace-entity-object-card__facts">
        <Fact label="Full Name" value={sessionTitle(session)} />
        <Fact label="Email" value={read(session, 'UserEmail', 'userEmail')} />
        <Fact label="Role" value={Array.isArray(roles) && roles.length ? roles.join(', ') : ''} />
        <Fact label="Credential type" value={read(session, 'CredentialType', 'credentialType')} />
        <Fact label="Status" value={read(session, 'Status', 'status')} />
        <Fact label="Session reference" value={activeSessionId(session)} />
        <Fact label="Client ID" value={read(session, 'ClientId', 'clientId')} />
        <Fact label="MCP Resource ID" value={read(session, 'ResourceConfigurationId', 'resourceConfigurationId')} />
        <Fact label="Provider mapping ID" value={read(session, 'ProviderMappingId', 'providerMappingId')} />
        <Fact label="OAuth client ID" value={read(session, 'OAuthClientId', 'oauthClientId')} />
        <Fact label="Application" value={read(session, 'ApplicationDisplayName', 'applicationDisplayName')} />
        <Fact label="MCP Server" value={read(session, 'McpServerDisplayName', 'mcpServerDisplayName')} />
        <Fact label="Environment" value={read(session, 'Environment', 'environment')} />
        <Fact label="Issued" value={formatDateTime(read(session, 'IssuedAtUtc', 'issuedAtUtc'))} />
        <Fact label="Last activity" value={formatDateTime(read(session, 'LastActivityAtUtc', 'lastActivityAtUtc'))} />
        <Fact label="Access expiration" value={formatDateTime(read(session, 'AccessExpiresAtUtc', 'accessExpiresAtUtc'))} />
        <Fact label="Idle expiration" value={formatDateTime(read(session, 'IdleExpiresAtUtc', 'idleExpiresAtUtc'))} />
        <Fact label="Absolute expiration" value={formatDateTime(read(session, 'AbsoluteExpiresAtUtc', 'absoluteExpiresAtUtc'))} />
        <Fact label="PAT label" value={read(session, 'PersonalAccessTokenLabel', 'personalAccessTokenLabel')} />
        <Fact label="PAT prefix" value={read(session, 'PersonalAccessTokenPrefix', 'personalAccessTokenPrefix')} />
        <Fact label="PAT fingerprint" value={read(session, 'PersonalAccessTokenFingerprint', 'personalAccessTokenFingerprint')} />
        <Fact label="Scopes" value={Array.isArray(scopes) && scopes.length ? scopes.join(', ') : ''} />
      </dl>
    </div>
  );
};

const ActiveSessionListControl = ({
  sessions = [],
  selectedSession = null,
  onSelectedSessionChange,
  searchText = '',
  onSearchChange,
  statusFilter = 'Active',
  onStatusFilterChange,
  onOrBefore = '',
  onOnOrBeforeChange,
  loading = false,
  loadingMore = false,
  error = '',
  hasMore = false,
  onLoadMore,
  resultGeneration = 0,
  emptyMessage = 'No active authentication sessions match this scope.',
}) => {
  const [searchDraft, setSearchDraft] = useState(searchText);
  const [detailSession, setDetailSession] = useState(null);

  useEffect(() => {
    setSearchDraft(searchText);
  }, [searchText]);

  useEffect(() => {
    onSelectedSessionChange?.(null);
    setDetailSession(null);
  }, [onSelectedSessionChange, resultGeneration]);

  useEffect(() => {
    if (searchDraft === searchText) return undefined;
    const timeout = window.setTimeout(() => {
      onSelectedSessionChange?.(null);
      onSearchChange?.(searchDraft);
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [
    onSearchChange,
    onSelectedSessionChange,
    searchDraft,
    searchText,
  ]);

  const selectedId = activeSessionId(selectedSession);
  const selectedKind = activeSessionKind(selectedSession);
  const showStatusFilter = typeof onStatusFilterChange === 'function';
  const showDateFilter = typeof onOnOrBeforeChange === 'function';
  const filterClassName = [
    'active-session-list__filters',
    !showStatusFilter && !showDateFilter
      ? 'active-session-list__filters--search-only'
      : '',
  ].filter(Boolean).join(' ');
  const visibleSessions = useMemo(
    () => (Array.isArray(sessions) ? sessions : []),
    [sessions],
  );
  return (
    <section className="active-session-list" aria-label="Authentication sessions">
      <div className={filterClassName}>
        <div className="active-session-list__search">
          <SearchInput
            value={searchDraft}
            onChange={setSearchDraft}
            placeholder="Search by user, email, application, server, scope, or token label..."
            disabled={loading}
          />
        </div>
        {showStatusFilter ? (
          <div className="active-session-list__status-filter">
            <FieldControl
              fieldMetadata={statusFilterField}
              value={statusFilter}
              onChange={(value, _fieldName, selectedItem) => onStatusFilterChange(
                normalizeStatusFilterValue(value, selectedItem),
              )}
              disabled={loading}
              disableError
            />
          </div>
        ) : null}
        {showDateFilter ? (
          <div className="active-session-list__date-filter">
            <FieldControl
              fieldMetadata={onOrBeforeField}
              value={onOrBefore}
              onChange={(value) => onOnOrBeforeChange(normalizeDateValue(value))}
              disabled={loading}
              disableError
            />
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="active-session-list__state active-session-list__state--error" role="alert">
          {error}
        </div>
      ) : null}
      {loading ? (
        <div className="active-session-list__state">
          <Loading />
          <span>Loading {statusFilter.toLowerCase()} authentication sessions...</span>
        </div>
      ) : null}
      {!loading && !error && visibleSessions.length === 0 ? (
        <div className="active-session-list__state">{emptyMessage}</div>
      ) : null}

      {!loading && visibleSessions.length ? (
        <div className="active-session-list__cards resource-workspace-record-list" role="listbox" aria-label={`${statusFilter} authentication sessions`}>
          {visibleSessions.map((session) => {
            const sessionId = activeSessionId(session);
            const kind = activeSessionKind(session);
            const isSelected = sessionId === selectedId && kind === selectedKind;
            const isActive = activeSessionIsActive(session);
            return (
              <article
                key={`${kind}:${sessionId}`}
                className={`active-session-list__card resource-workspace-record-row${isSelected ? ' active-session-list__card--selected' : ''}`}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => onSelectedSessionChange?.(session)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  onSelectedSessionChange?.(session);
                }}
                aria-label={`${read(session, 'CredentialType', 'credentialType') || 'Authentication'} session for ${sessionTitle(session)}`}
              >
                <div className="active-session-list__identity">
                  <span className="resource-workspace-record-row-descriptive-value">
                    {read(session, 'CredentialType', 'credentialType') || 'Authentication'}
                  </span>
                  <strong>{sessionTitle(session)}</strong>
                  <span>{sessionSubtitle(session)}</span>
                </div>
                <dl className="active-session-list__summary resource-workspace-entity-object-card__facts">
                  <Fact
                    label="Last activity"
                    value={formatDateTime(read(session, 'LastActivityAtUtc', 'lastActivityAtUtc'))}
                  />
                  <Fact
                    label="Idle expiration"
                    value={formatDateTime(read(session, 'IdleExpiresAtUtc', 'idleExpiresAtUtc'))}
                  />
                  <Fact
                    label="Absolute expiration"
                    value={formatDateTime(read(session, 'AbsoluteExpiresAtUtc', 'absoluteExpiresAtUtc'))}
                  />
                  <Fact
                    label="MCP Server"
                    value={read(session, 'McpServerDisplayName', 'mcpServerDisplayName')}
                  />
                </dl>
                <div className="active-session-list__card-actions resource-workspace-record-row-actions resource-workspace-record-row-actions--stacked-status">
                  <span className={`resource-workspace-status-pill ${isActive ? 'resource-workspace-status-pill--success' : 'resource-workspace-status-pill--muted'}`}>
                    {read(session, 'Status', 'status') || (isActive ? 'Active' : 'Inactive')}
                  </span>
                  <ButtonControl
                    type="standard-small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setDetailSession(session);
                    }}
                    aria-label={`View ${sessionTitle(session)} session details`}
                  >
                    <IconControl iconKey="view" width="1.25rem" height="1.25rem" />
                  </ButtonControl>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      {hasMore && !loading ? (
        <div className="active-session-list__load-more">
          <ButtonControl
            type="standard"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? <Loading type="spinner-2" size="1rem" /> : null}
            {loadingMore ? 'Loading...' : 'Load more'}
          </ButtonControl>
        </div>
      ) : null}

      <DialogControl
        openDialog={Boolean(detailSession)}
        onCancel={() => setDetailSession(null)}
        onOk={() => setDetailSession(null)}
        title="Authentication Session Details"
        okText="Close"
        hideCancel
        hideCancelButton
      >
        <ActiveSessionDetails session={detailSession} />
      </DialogControl>
    </section>
  );
};

export default ActiveSessionListControl;
